import {
  type MetaFilterProps,
  useFilterSets,
} from '@kizen/filters/hooks/use-filter-sets';
import type { FilterVariableEntry, Instance } from '@kizen/filters/variables';
import { FILTER_TYPES } from 'components/Wizards/MetaFilters/types';
import {
  useState,
  PropsWithChildren,
  createContext,
  useContext,
  SetStateAction,
  Dispatch,
  useCallback,
  useRef,
} from 'react';

import { useFilterErrors } from 'ts-components/filters';

import { selectFilterMetadataDefinition } from 'store/filterMetaData/selectors';
import { useSelector } from 'react-redux';
import { isValidPhoneNumber } from 'libphonenumber-js/max';
import { validate as validateEmail } from 'components/Inputs/TextInput/presets/EmailAddress';
import type { FilterGroup } from '@kizen/api/custom-object';

type ConditionTypeOptions = {
  label: string;
  value: string;
  image: any;
};

export type FilterGroupOption = {
  label: string;
  value: string;
  group: FilterGroup;
};

type FiterPropFilter = {
  and: boolean;
  config: any;
};

export type FilterProps = {
  filter: FiterPropFilter;
  isForCustomObject: boolean;
  customObject: any;
  values: any;
  setValues: any;
  state: any;
  conditionTypeOptions: ConditionTypeOptions[];
  groupOptions: symbol | FilterGroupOption[];
  objectGroups: FilterGroup[] | undefined;
  initialFilterElement: any;
  disabled: boolean;
  loading: boolean;
  valid: boolean;
  originalFilterState: any;
  setFilter(): void;
};

export type FilterWizardContextProps = {
  filterProps: FilterProps | null;
  isClient: boolean;
  objectId?: string | null;
  clientObject?: any;
  instance?: Instance;
  variables: FilterVariableEntry[];
};

type SetFilterHasErrorsProps = {
  errors: Record<string, ({ [k: string]: string } | null)[]>;
  hasErrors: boolean;
};
interface WizardFilterContextType {
  instance?: Instance;
  filterSets: MetaFilterProps['filterSets'];
  filterOps: MetaFilterProps['filterOps'];

  filterErrors: SetFilterHasErrorsProps['errors'];
  setFilterHasErrors(props: SetFilterHasErrorsProps): void;
  hasErrors?: boolean;
  setHasErrors: Dispatch<SetStateAction<boolean>>;
  operation: string | undefined | null;
  setOperation: Dispatch<SetStateAction<string>>;

  // from filterProps
  setValues: Dispatch<SetStateAction<any>>;
  conditionTypeOptions: ConditionTypeOptions[] | null | undefined;
  initialFilterElement: object | null;
  groupOptions: symbol | FilterGroupOption[] | null | undefined;
  objectGroups: FilterGroup[] | undefined;
  loading: boolean;
  valid: boolean;
  values: object | null;
  filter: FiterPropFilter | null;
  isForCustomObject: boolean;
  customObject: object | null;
  originalFilterState: object | null;
  setFilter(): void;
}

const ctx = createContext<WizardFilterContextType>({
  filterSets: null,
  filterOps: null,
  filterErrors: {},
  setFilterHasErrors: () => null,
  hasErrors: false,
  setHasErrors: () => null,
  operation: null,
  setOperation: () => null,
  // from filterProps
  setValues: () => null,
  conditionTypeOptions: null,
  initialFilterElement: null,
  groupOptions: [],
  objectGroups: [],
  loading: false,
  valid: true,
  values: null,
  filter: null,
  isForCustomObject: false,
  customObject: null,
  originalFilterState: null,
  setFilter: () => null,
});

export const useWizardFilterContext = () => useContext(ctx);

export const WizardFilterContext = ({
  filterProps,
  isClient,
  clientObject,
  variables,
  instance,
  children,
}: PropsWithChildren<FilterWizardContextProps>) => {
  const metadata = useSelector(selectFilterMetadataDefinition);
  const prevVariablesRef = useRef(variables);
  const [filterErrors, setFilterErrors] = useFilterErrors();
  const [hasErrors, setHasErrors] = useState(false);
  const setFilterHasErrors = useCallback(
    ({ errors, hasErrors }: SetFilterHasErrorsProps) => {
      setFilterErrors(errors);
      setHasErrors(hasErrors);
    },
    [setFilterErrors, setHasErrors]
  );

  const [operation, setOperation] = useState(
    filterProps?.filter?.and ? 'and' : 'or'
  );

  const [filterSets, filterOps] = useFilterSets(
    metadata,
    variables,
    isValidPhoneNumber,
    validateEmail.withDomain,
    filterProps?.filter?.config
  );

  if (prevVariablesRef.current !== variables) {
    filterOps.reset();
    prevVariablesRef.current = variables;
  }

  const data = {
    instance,
    filterSets,
    filterOps,
    filterErrors,
    setFilterHasErrors,
    hasErrors,
    setHasErrors,
    operation,
    setOperation,
    setValues: filterProps?.setValues,
    conditionTypeOptions: filterProps?.conditionTypeOptions || [],
    initialFilterElement: filterProps?.initialFilterElement,
    groupOptions: filterProps?.groupOptions || [],
    objectGroups: filterProps?.objectGroups || [],
    loading: filterProps?.loading || false,
    valid:
      filterProps?.valid || filterProps?.values?.type === FILTER_TYPES.CUSTOM,
    values: filterProps?.values,
    filter: filterProps?.values?.filter,
    isForCustomObject: filterProps?.isForCustomObject || false,
    customObject: isClient ? clientObject : filterProps?.customObject,
    originalFilterState: filterProps?.originalFilterState,
    setFilter: filterProps?.setFilter || (() => null),
  };

  return <ctx.Provider value={data}>{children}</ctx.Provider>;
};
